/**
 * useWebDateInputStyle — the style a web date/time input needs to read as one
 * of the app's inputs.
 *
 * On web `@react-native-community/datetimepicker` is a shim
 * (utils/web-mocks/datetimepicker.web.js) that renders a raw
 * <input type="date|datetime-local|time"> styled with only padding and a gray
 * border, so it takes the BROWSER's form-control face (13.33px system UI) and
 * colors (white box, black text) instead of the app's — and sits next to the
 * themed fields in a visibly different font. This hands it the body type and
 * the input tokens.
 *
 * The shim spreads the object straight onto the DOM node, so:
 * - CSS longhands only — RN's paddingHorizontal/Vertical mean nothing there;
 * - `color-scheme` rides along (DOM CSS, not an RN style, spread past
 *   StyleSheet's key check) so Chrome's calendar icon stays legible on the
 *   dark input.
 *
 * Returns undefined on native: the same style would restyle the iOS wheel and
 * the Android dialog.
 */
import { useMemo } from 'react';
import { Platform, type TextStyle } from 'react-native';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';

export function useWebDateInputStyle(): TextStyle | undefined {
  const { colors, semantic, mode } = useThemeColors();
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();

  return useMemo<TextStyle | undefined>(() => {
    if (Platform.OS !== 'web') return undefined;
    return {
      ...type.body,
      color: colors.text.primary,
      backgroundColor: semantic.background.input,
      borderColor: semantic.border.input,
      borderWidth: 1,
      borderRadius: radius.md,
      paddingTop: space.sm,
      paddingBottom: space.sm,
      paddingLeft: space.md,
      paddingRight: space.md,
      ...({ colorScheme: mode } as object),
    };
  }, [colors, semantic, mode, type, space, radius]);
}
